import React, { useState, useEffect, useRef } from 'react';
import { api } from '../../App';
import toast from 'react-hot-toast';

export default function Branding() {
  const [b, setB] = useState(null);
  const [conn, setConn] = useState([]);
  const [saving, setSaving] = useState(false);
  const logoRef = useRef();
  const heroRef = useRef();
  const photoRef = useRef();

  const load = async () => {
    try {
      const [r, c] = await Promise.all([api.get('/api/hiring/branding'), api.get('/api/hiring/connectors')]);
      setB(r.data); setConn(c.data.connectors || []);
    } catch { toast.error('Failed to load branding'); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try { const r = await api.put('/api/hiring/branding', b); setB(r.data); toast.success('Branding saved'); }
    catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const upload = async (file, field) => {
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    const t = toast.loading('Uploading…');
    try {
      const r = await api.post('/api/hiring/branding/upload', fd);
      if (field === 'photos') setB(p => ({ ...p, photos: [...(p.photos || []), r.data.url] }));
      else setB(p => ({ ...p, [field]: r.data.url }));
      toast.success('Uploaded', { id: t });
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed', { id: t }); }
  };

  if (!b) return <div className="py-16 text-center text-text-muted text-sm"><i className="fa-solid fa-circle-notch fa-spin text-2xl block mb-3" />Loading…</div>;
  const base = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <div className="space-y-5" data-testid="branding-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-muted">Style your public careers site — applicants see this at <code className="text-primary">{base}/careers</code></p>
        <div className="flex gap-2">
          <a href={`${base}/careers`} target="_blank" rel="noreferrer" className="btn-secondary text-sm" data-testid="preview-careers-btn"><i className="fa-solid fa-arrow-up-right-from-square mr-1.5" />Preview</a>
          <button onClick={save} disabled={saving} className="btn-primary text-sm" data-testid="save-branding-btn">{saving ? <i className="fa-solid fa-circle-notch fa-spin mr-1.5" /> : <i className="fa-solid fa-floppy-disk mr-1.5" />}Save</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Content */}
        <div className="bg-surface rounded-2xl border border-border p-6 space-y-4">
          <p className="text-xs font-bold uppercase text-text-secondary"><i className="fa-solid fa-pen-nib mr-1.5 text-primary" />Content</p>
          <label className="block text-[10px] font-semibold uppercase text-text-muted">Company name
            <input value={b.company_name || ''} onChange={e => setB({ ...b, company_name: e.target.value })} className="input-field text-sm mt-1" data-testid="brand-company-input" /></label>
          <label className="block text-[10px] font-semibold uppercase text-text-muted">Headline
            <input value={b.headline || ''} onChange={e => setB({ ...b, headline: e.target.value })} className="input-field text-sm mt-1" data-testid="brand-headline-input" /></label>
          <label className="block text-[10px] font-semibold uppercase text-text-muted">Tagline
            <textarea value={b.tagline || ''} onChange={e => setB({ ...b, tagline: e.target.value })} className="input-field text-sm mt-1 h-20" data-testid="brand-tagline-input" /></label>
          <label className="block text-[10px] font-semibold uppercase text-text-muted">Primary color
            <div className="flex items-center gap-2 mt-1">
              <input type="color" value={b.primary_color || '#10b981'} onChange={e => setB({ ...b, primary_color: e.target.value })} className="w-12 h-10 rounded-lg border border-border cursor-pointer" data-testid="brand-color-input" />
              <input value={b.primary_color || ''} onChange={e => setB({ ...b, primary_color: e.target.value })} className="input-field text-sm font-mono" />
            </div>
          </label>
        </div>

        {/* Images */}
        <div className="bg-surface rounded-2xl border border-border p-6 space-y-4">
          <p className="text-xs font-bold uppercase text-text-secondary"><i className="fa-solid fa-images mr-1.5 text-primary" />Logo &amp; Photos</p>
          <div>
            <p className="text-[10px] font-semibold uppercase text-text-muted mb-1">Logo</p>
            <div className="flex items-center gap-3">
              {b.logo_url ? <img src={b.logo_url} alt="logo" className="h-10 rounded bg-slate-800 p-1" /> : <span className="text-xs text-text-muted">No logo</span>}
              <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={e => upload(e.target.files[0], 'logo_url')} data-testid="logo-upload-input" />
              <button onClick={() => logoRef.current.click()} className="btn-secondary text-xs"><i className="fa-solid fa-upload mr-1" />Upload</button>
              {b.logo_url && <button onClick={() => setB({ ...b, logo_url: '' })} className="text-rose-500 text-xs">Remove</button>}
            </div>
            <input placeholder="…or paste image URL" value={b.logo_url || ''} onChange={e => setB({ ...b, logo_url: e.target.value })} className="input-field text-xs mt-2" />
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase text-text-muted mb-1">Hero background image</p>
            <div className="flex items-center gap-3">
              {b.hero_image_url ? <img src={b.hero_image_url} alt="hero" className="h-12 w-20 object-cover rounded" /> : <span className="text-xs text-text-muted">None</span>}
              <input ref={heroRef} type="file" accept="image/*" className="hidden" onChange={e => upload(e.target.files[0], 'hero_image_url')} data-testid="hero-upload-input" />
              <button onClick={() => heroRef.current.click()} className="btn-secondary text-xs"><i className="fa-solid fa-upload mr-1" />Upload</button>
              {b.hero_image_url && <button onClick={() => setB({ ...b, hero_image_url: '' })} className="text-rose-500 text-xs">Remove</button>}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase text-text-muted mb-1">Company photos</p>
            <div className="flex flex-wrap gap-2">
              {(b.photos || []).map((p, i) => (
                <div key={i} className="relative group"><img src={p} alt="" className="h-14 w-20 object-cover rounded-lg" /><button onClick={() => setB({ ...b, photos: b.photos.filter((_, idx) => idx !== i) })} className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white w-5 h-5 rounded-full text-[10px]"><i className="fa-solid fa-xmark" /></button></div>
              ))}
              <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={e => upload(e.target.files[0], 'photos')} data-testid="photo-upload-input" />
              <button onClick={() => photoRef.current.click()} className="h-14 w-20 rounded-lg border-2 border-dashed border-border text-text-muted hover:border-primary text-xs"><i className="fa-solid fa-plus" /></button>
            </div>
          </div>
        </div>
      </div>

      {/* Connector status */}
      <div className="bg-surface rounded-2xl border border-border p-6">
        <p className="text-xs font-bold uppercase text-text-secondary mb-3"><i className="fa-solid fa-plug mr-1.5 text-primary" />Job-Board Auto-Post Connectors</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {conn.map(c => (
            <div key={c.platform} className="bg-surface-alt rounded-xl p-4" data-testid={`connector-${c.platform}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-text-primary">{c.label}</span>
                <span className={`w-2.5 h-2.5 rounded-full ${c.configured ? 'bg-emerald-500' : 'bg-slate-300'}`} />
              </div>
              <p className="text-[11px] text-text-muted mt-1">{c.configured ? 'Connected — auto-posts on publish' : c.env_key ? `Add ${c.env_key} to connect` : 'Auto (careers page)'}</p>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-text-muted mt-3">Google Jobs indexes your public careers page automatically. For Indeed/LinkedIn/ZipRecruiter, add the partner API key to enable one-click auto-posting; until then, publishing gives you tailored copy + a shareable link.</p>
      </div>
    </div>
  );
}
